/**
 * ChecklistEditor — éditeur de checklist d'un état des lieux (mig 215).
 *
 * Chaque point : libellé + état (OK / à surveiller / dégradé) + note + coût
 * estimé (affiché seulement si dégradé). Les points dégradés avec coût sont ceux
 * qui justifient un devis (bouton « Demander un devis » de la fiche).
 *
 * Composant contrôlé : reçoit la liste + un onChange.
 */
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import type { ChecklistItem, ChecklistEtat } from '@roronoa-collectivites/shared-types';

const ETATS: { value: ChecklistEtat; label: string; classe: string }[] = [
  { value: 'ok', label: 'OK', classe: 'bg-success/20 text-success border-success/40' },
  { value: 'a_surveiller', label: 'À surveiller', classe: 'bg-warning/20 text-warning border-warning/40' },
  { value: 'degrade', label: 'Dégradé', classe: 'bg-danger/20 text-danger border-danger/40' },
];

let seq = 0;
function newId() { return `pt-${Date.now()}-${seq++}`; }

export function ChecklistEditor({ items, onChange, readOnly = false }: {
  items: ChecklistItem[];
  onChange: (items: ChecklistItem[]) => void;
  readOnly?: boolean;
}) {
  function update(id: string, patch: Partial<ChecklistItem>) {
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }
  function ajouter() {
    onChange([...items, { id: newId(), libelle: '', etat: 'ok', note: '', coutEstime: null }]);
  }
  function retirer(id: string) {
    onChange(items.filter((it) => it.id !== id));
  }

  const totalCout = items.filter((i) => i.etat === 'degrade' && i.coutEstime).reduce((s, i) => s + (i.coutEstime ?? 0), 0);

  return (
    <div className="space-y-2">
      {items.length === 0 && <p className="text-sm text-ink-subtle">Aucun point pour l'instant.</p>}

      {items.map((it) => (
        <div key={it.id} className="rounded-r-md border border-border bg-surface/40 p-2.5 space-y-2">
          <div className="flex items-start gap-2">
            <input
              value={it.libelle}
              onChange={(e) => update(it.id, { libelle: e.target.value })}
              readOnly={readOnly}
              placeholder="Point inspecté (ex : mur nord, sol, sanitaires…)"
              className="h-9 flex-1 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink"
            />
            {!readOnly && (
              <button type="button" onClick={() => retirer(it.id)} className="mt-1 text-ink-subtle hover:text-danger" aria-label="Retirer">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {ETATS.map((e) => {
              const on = it.etat === e.value;
              return (
                <button
                  key={e.value}
                  type="button"
                  disabled={readOnly}
                  onClick={() => update(it.id, { etat: e.value })}
                  className={`rounded-r-pill border px-2.5 py-1 text-xs font-medium transition-colors ${on ? e.classe : 'border-border text-ink-muted hover:bg-surface/60'}`}
                >
                  {e.label}
                </button>
              );
            })}
          </div>

          {(it.etat === 'degrade' || it.etat === 'a_surveiller') && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_140px]">
              <input
                value={it.note ?? ''}
                onChange={(e) => update(it.id, { note: e.target.value })}
                readOnly={readOnly}
                placeholder="Précision (nature de la dégradation…)"
                className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink"
              />
              {it.etat === 'degrade' && (
                <div className="relative">
                  <input
                    type="number" min={0} step={10}
                    value={it.coutEstime ?? ''}
                    onChange={(e) => update(it.id, { coutEstime: e.target.value === '' ? null : Number(e.target.value) })}
                    readOnly={readOnly}
                    placeholder="Coût estimé"
                    className="h-9 w-full rounded-r-md border border-border bg-surface/60 pl-2 pr-6 text-sm text-ink"
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-ink-subtle">€</span>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {!readOnly && (
        <button type="button" onClick={ajouter} className="inline-flex items-center gap-1.5 text-sm text-tech-fg hover:underline">
          <Plus className="h-4 w-4" /> Ajouter un point
        </button>
      )}

      {totalCout > 0 && (
        <p className="flex items-center gap-1.5 pt-1 text-sm text-ink">
          <AlertTriangle className="h-4 w-4 text-danger" aria-hidden="true" />
          Coût estimé des dégradations : <span className="font-semibold">{totalCout.toLocaleString('fr-FR')} €</span>
        </p>
      )}
    </div>
  );
}
